/**
 * Design Domain Types
 * 
 * SOLID Principles Applied:
 * - Interface Segregation: Split into specific interfaces for different use cases
 * - Single Responsibility: Each interface has a single, clear purpose
 * 
 * These types match the backend NestJS DTOs and entities
 */

// Enum matching backend
export enum DesignStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  SOLD = 'sold',
}

export enum DesignSide {
  FRONT = 'front',
  BACK = 'back',
}

/**
 * Base Design interface - read operations
 */
export interface IDesign {
  id: string;
  title: string;
  description?: string;
  /**
   * Seller/designer chosen default mockup color key.
   * Example: "white" | "black" | "dark-gray" | "navy"
   */
  defaultColor?: string | null;
  frontSide: Record<string, any>; // Fabric.js JSON
  backSide: Record<string, any>;  // Fabric.js JSON
  frontPreviewUrl: string;
  backPreviewUrl: string;
  frontMockupUrl?: string | null;
  backMockupUrl?: string | null;
  /**
   * Color-aware mockups generated server-side.
   * Keys match backend folder names under api/assets/mockups/tshirt/{color}/
   * Example: "navy", "dark-gray", "white", "black"
   */
  mockupUrlsByColor?: Record<string, { front?: string; back?: string }> | null;
  mockupVariantsByColor?: Record<
    string,
    {
      front?: {
        original?: string;
        thumbnail?: string;
        small?: string;
        medium?: string;
        large?: string;
      };
      back?: {
        original?: string;
        thumbnail?: string;
        small?: string;
        medium?: string;
        large?: string;
      };
    }
  > | null;
  /**
   * Custom mockups uploaded by seller (per product + color).
   * Format: { [productId]: { [color]: { front?: string[], back?: string[], uploadedAt?: isoDate } } }
   * Each side can have multiple mockup URLs (array) to allow sellers to upload multiple mockups.
   * These take priority over system-generated mockups when available.
   */
  customMockupsByProductAndColor?: Record<
    string, // productId
    Record<
      string, // color
      {
        front?: string[]; // Array of URLs - allows multiple mockups per product+color+side
        back?: string[]; // Array of URLs - allows multiple mockups per product+color+side
        uploadedAt?: string;
      }
    >
  > | null;
  status: DesignStatus;
  creatorCommissionEligible?: boolean;
  tags: string[];
  views: number;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    /** Omitted on public PDP payloads */
    email?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    username?: string;
    storeSlug?: string | null;
    avatar?: string;
    image?: string;
  };
}

/**
 * DTO for creating a new design (matches backend CreateDesignDto)
 */
export interface ICreateDesignDto {
  title: string;
  defaultSide: DesignSide;
  productType: string;
  color?: string;
  frontSide: Record<string, any>;
  backSide: Record<string, any>;
  frontPreviewUrl?: string; // Generated preview image for front
  backPreviewUrl?: string;  // Generated preview image for back
  metadata?: Record<string, any>;
}

/**
 * DTO for updating an existing design
 */
export interface IUpdateDesignDto {
  // No id field - it comes from URL parameter
  title?: string;
  description?: string;
  frontSide?: Record<string, any>;
  backSide?: Record<string, any>;
  frontPreviewUrl?: string;
  backPreviewUrl?: string;
  status?: DesignStatus;
  tags?: string[];
}

/**
 * Request payload for saving design from canvas
 * This is what our editor will send
 */
export interface ISaveDesignPayload {
  title: string;
  frontCanvasJSON: Record<string, any>;
  backCanvasJSON: Record<string, any>;
  frontPreview: string; // base64 or URL
  backPreview: string;  // base64 or URL
  productType?: string;
  color?: string;
}

/**
 * API Response wrapper
 */
export interface IApiResponse<T> {
  data: T;
  message?: string;
}

/**
 * Paginated response for design lists
 */
export interface IPaginatedDesigns {
  data: IDesign[];
  total: number;
  page: number;
  pageSize: number;
}



